import { projectAtom } from "apps/labelstudio/src/providers/ProjectProvider";
import { useAtom } from "jotai";
import React, { useEffect, useRef } from "react";
import { useAPI } from "../../../providers/ApiProvider";

/**
 * Owns the lifecycle of a brand-new "draft" project created by the wizard.
 *
 * Label Studio's wizard needs a real project id server-side from the moment
 * you open it (imports, config validation, sample tasks all key off it), so
 * this hook creates the row up-front with a placeholder title. If the user
 * closes / refreshes / navigates away WITHOUT calling ``markSaved`` the hook
 * quietly DELETEs the draft so abandoned wizards don't litter the DB.
 */
export const useDraftProject = () => {
  const api = useAPI();
  const [project, setProject] = useAtom(projectAtom);
  // Track the draft's id separately from React state so async handlers and
  // the beforeunload listener can see the latest value without stale closures.
  const draftIdRef = useRef(null);
  const savedRef = useRef(false);

  const markSaved = React.useCallback(() => {
    savedRef.current = true;
  }, []);

  const discardDraft = React.useCallback(async () => {
    const id = draftIdRef.current;
    if (!id || savedRef.current) return;
    draftIdRef.current = null;
    try {
      await api.callApi("deleteProject", { params: { pk: id }, suppressError: true });
    } catch {
      /* best-effort */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchDraftProject = React.useCallback(async () => {
    // If the wizard is running inside the Capacitor mobile bundle,
    // default ``allow_on_mobile`` to true so a mobile-created project
    // is immediately annotatable on mobile without the operator
    // hunting through Settings → General to flip the switch. Web /
    // desktop keeps the model default (false) so existing behaviour is
    // preserved — this only changes the flag when the CREATION context
    // is the mobile app itself.
    // ``document.body.classList.contains("is-capacitor")`` is set
    // pre-hydration by native/mobile/spa-template/index.html
    // (``<body class="is-capacitor">``), and again defensively by
    // main.tsx, so it is present by the time the wizard mounts.
    const isMobileCreation = typeof document !== "undefined" && document.body?.classList?.contains("is-capacitor");
    // Omit ``title`` from the body entirely rather than sending "".
    // ``ProjectSerializer.validate_title`` (projects/serializers.py) is a
    // FIELD-LEVEL validator that fires whenever ``title`` is present in
    // the payload and rejects the empty string as "Title cannot be
    // blank." — killing the draft POST. The OBJECT-LEVEL guard
    // (``validate()``) explicitly allows a missing title when
    // ``is_draft: true``: it only requires title on the final
    // ``is_draft: false`` PATCH. So the correct wire shape for a draft
    // is a body WITHOUT the title key at all.
    const draft = await api.callApi("createProject", {
      body: {
        is_draft: true,
        ...(isMobileCreation ? { allow_on_mobile: true } : {}),
      },
    });
    if (draft) {
      draftIdRef.current = draft.id;
      setProject(draft);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchDraftProject();
    // Browser close/refresh: synchronously delete via keepalive fetch so the
    // request survives page unload.
    const handleUnload = () => {
      const id = draftIdRef.current;
      if (!id || savedRef.current) return;
      try {
        const url = `/api/projects/${id}`;
        const headers = { "Content-Type": "application/json" };
        fetch(url, { method: "DELETE", keepalive: true, credentials: "same-origin", headers });
      } catch {
        /* best-effort */
      }
    };
    window.addEventListener("beforeunload", handleUnload);
    return () => {
      window.removeEventListener("beforeunload", handleUnload);
      // SPA navigation away from the wizard — clean up via normal API call.
      discardDraft();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { project, setProject, markSaved, discardDraft };
};
